import { makeAssistantToolUI } from '@assistant-ui/react';
import { thinkToolSignature } from '@andsend/services/agent/shared';
import { z } from 'zod';
import { cn } from '@shadcn/ui/lib/utils';
import { ChevronDownIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useState, useEffect, useRef } from 'react';
import { ScrollArea } from '@shadcn/ui/components/ui/scroll-area';

export const ThinkingToolUI = makeAssistantToolUI<
    z.infer<typeof thinkToolSignature.inputSchema>,
    z.infer<typeof thinkToolSignature.outputSchema>
>({
    toolName: thinkToolSignature.name,
    render: ({ args, status }) => {
        const [isCollapsed, setIsCollapsed] = useState(status.type !== 'running');
        const [prevText, setPrevText] = useState('');
        const [renderedDeltas, setRenderedDeltas] = useState<string[]>([]);
        const deltaQueue = useRef<string[]>([]);
        const animating = useRef(false);
        const lastChunkElementRef = useRef<HTMLSpanElement>(null);
        const isRunning = status.type === 'running';
        const text = args?.thought ?? '';
        console.log('🤖 ~ text:', text, Date.now());

        useEffect(() => {
            if (lastChunkElementRef.current) {
                lastChunkElementRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, [renderedDeltas]);

        useEffect(() => {
            if (status.type === 'complete' && !isCollapsed) {
                setTimeout(() => {
                    setIsCollapsed(true);
                }, 2000);
            } else if (isRunning) {
                setIsCollapsed(false);
            }
        }, [status.type]);

        const getToolState = () => {
            if (status.type === 'complete') {
                return 'Analyzed new data';
            }
            return 'Analyzing...';
        };

        // Detect new delta(s) and queue them
        useEffect(() => {
            if (!text.startsWith(prevText)) {
                // Reset if text is replaced or cleared
                setPrevText(text);
                setRenderedDeltas([]);
                deltaQueue.current = [];
                animating.current = false;
                return;
            }
            if (text !== prevText) {
                const newDelta = text.slice(prevText.length);
                if (newDelta.length > 0) {
                    deltaQueue.current.push(newDelta);
                    processQueue();
                }
            }
        }, [text, prevText]);

        // Process the queue of deltas
        const processQueue = () => {
            if (animating.current) return;
            if (deltaQueue.current.length === 0) return;
            animating.current = true;
            const nextDelta = deltaQueue.current.shift()!;
            setRenderedDeltas((prev) => [...prev, nextDelta]);
            setTimeout(() => {
                setPrevText((prev) => prev + nextDelta);
                animating.current = false;
                if (deltaQueue.current.length > 0) {
                    processQueue();
                }
            }, 200); // 0.5s = 500ms
        };

        // If prevText is reset, clear queue and deltas
        useEffect(() => {
            if (!text.startsWith(prevText)) {
                setRenderedDeltas([]);
                deltaQueue.current = [];
                animating.current = false;
            }
        }, [text, prevText]);

        return (
            <div className="mb-4 flex w-full flex-col pt-1 animate-fade-in-0.5">
                <div
                    className="flex items-center gap-2 py-2 cursor-pointer hover:bg-muted/30 rounded-md text-muted-foreground"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                >
                    <span className={cn('text-sm flex items-center gap-2')}>{getToolState()}</span>
                    <div className="grow" />
                    <motion.div animate={{ rotate: isCollapsed ? 0 : 180 }} transition={{ duration: 0.2 }}>
                        <ChevronDownIcon className="h-4 w-4" />
                    </motion.div>
                </div>
                <motion.div
                    initial={false}
                    animate={{ height: isCollapsed ? 0 : 'auto', opacity: isCollapsed ? 0 : 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    style={{ overflow: 'hidden' }}
                    className="border-l-2 ml-6 mt-1"
                >
                    <ScrollArea className="max-h-36">
                        <div className="px-4 text-sm text-muted-foreground whitespace-pre-line">{prevText}</div>
                    </ScrollArea>
                </motion.div>
            </div>
        );
    },
});
